import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Save,
  Upload,
  Eye,
  Trash2,
  Link,
  Clock,
  User,
  MessageSquare,
  Pencil,
  Key,
  UserPlus,
  UserMinus,
  Info,
  FileText,
  Shield,
  Globe,
  Server,
  Database,
  FileCode,
  HardDrive,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  EyeOff,
  Plus,
  ExternalLink,
  Folder,
  Edit,
  Calendar
} from "lucide-react";
import { getFileIcon, getFileIconColor } from "../utils/fileIcons";
import { useProject } from "../contexts/ProjectContext";
import { useNotification } from "../contexts/NotificationContext";
import { useUser } from "../contexts/UserContext";
import { useSocket } from "../contexts/SocketContext";
import { projectAPI, activityAPI, categoryAPI } from "../utils/api";
import SimpleQuillEditor from "./SimpleQuillEditor";
import Avatar from "./Avatar";
import ConfirmationModal from "./ConfirmationModal";
import CustomDatePicker from "./CustomDatePicker";
import CustomStatusDropdown from "./CustomStatusDropdown";
import { encryptForBackend } from "../utils/encryption";

const EditProjectModal = ({ project, onClose }) => {
  const { updateProject } = useProject();
  const { showToast } = useNotification();
  const { user } = useUser();
  const { socket, joinProject, leaveProject } = useSocket();
  const isAdmin = user?.role === "admin";
  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);
  const startDateInputRef = useRef(null);
  const endDateInputRef = useRef(null);
  const startDatePickerRef = useRef(null);
  const endDatePickerRef = useRef(null);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [showActivities, setShowActivities] = useState(false);
  const [editMode, setEditMode] = useState(true);
  const [rsaPublicKey, setRsaPublicKey] = useState(null);
  const [showStartDatePicker, setShowStartDatePicker] = useState(false);
  const [showEndDatePicker, setShowEndDatePicker] = useState(false);
  const [isUserEditing, setIsUserEditing] = useState(true); // Prevent auto-updates while modal is open

  // Mark modal as being edited (prevent socket/external updates from interfering)
  useEffect(() => {
    setIsUserEditing(true);
    return () => {
      setIsUserEditing(false);
    };
  }, []);

  // Fetch RSA Public Key for E2EE
  useEffect(() => {
    const fetchPublicKey = async () => {
      try {
        const response = await projectAPI.getPublicKey();
        if (response.data.success) {
          setRsaPublicKey(response.data.publicKey);
        }
      } catch (error) {
        console.error("Failed to fetch RSA public key:", error);
      }
    };
    fetchPublicKey();
  }, []);

  // Tab state
  const [activeTab, setActiveTab] = useState("info"); // "info" or "credentials"

  // Credentials state
  const [credentials, setCredentials] = useState([]);
  const [credentialAccess, setCredentialAccess] = useState([]);
  const [newCredential, setNewCredential] = useState({ label: "", value: "" });
  const [credentialLoading, setCredentialLoading] = useState(false);
  const [savingCredentialKey, setSavingCredentialKey] = useState(null); // Track which specific credential is being saved
  const [expandedCategories, setExpandedCategories] = useState({});
  const [copiedField, setCopiedField] = useState(null);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [localValues, setLocalValues] = useState({}); // Local state for input values to prevent reset on save/delete
  const [newCmsField, setNewCmsField] = useState({ label: "", value: "" }); // CMS custom field state
  const [showCmsCustomField, setShowCmsCustomField] = useState(false); // Toggle CMS custom field form

  // Description notes state
  const [descriptions, setDescriptions] = useState([]);
  const [newDescription, setNewDescription] = useState("");
  const [editingDescriptionId, setEditingDescriptionId] = useState(null);
  const [editingDescriptionContent, setEditingDescriptionContent] =
    useState("");
  const [descriptionLoading, setDescriptionLoading] = useState(false);

  // Categories state
  const [categories, setCategories] = useState([]);

  // Credential categories and templates
  const credentialCategories = {
    domain: {
      name: "Domain & Project",
      icon: Globe,
      color: "blue",
      fields: [
        { label: "Project URL", key: "project_url" },
        { label: "Domain", key: "domain" },
        { label: "Domain Username", key: "domain_username" },
        { label: "Domain Password", key: "domain_password", isPassword: true },
      ],
    },
    hosting: {
      name: "Hosting",
      icon: Server,
      color: "green",
      fields: [
        { label: "Hosting Provider", key: "hosting" },
        { label: "Hosting Username", key: "hosting_username" },
        {
          label: "Hosting Password",
          key: "hosting_password",
          isPassword: true,
        },
      ],
    },
    cms: {
      name: "CMS",
      icon: FileCode,
      color: "purple",
      fields: [
        { label: "CMS URL", key: "cms_url" },
        { label: "CMS Username", key: "cms_username" },
        { label: "CMS Password", key: "cms_password", isPassword: true },
      ],
    },
    database: {
      name: "Database",
      icon: Database,
      color: "orange",
      fields: [
        { label: "Database URL", key: "database_url" },
        { label: "Database Name", key: "database_name" },
        {
          label: "Database Password",
          key: "database_password",
          isPassword: true,
        },
      ],
    },
    ftp: {
      name: "FTP",
      icon: HardDrive,
      color: "cyan",
      fields: [
        { label: "FTP Hostname", key: "ftp_hostname" },
        { label: "FTP Username", key: "ftp_username" },
        { label: "FTP Password", key: "ftp_password", isPassword: true },
      ],
    },
    ssl: {
      name: "SSL",
      icon: Shield,
      color: "emerald",
      fields: [{ label: "SSL Certificate", key: "ssl" }],
    },
    other: {
      name: "Other Credentials",
      icon: Key,
      color: "gray",
      fields: [],
    },
  };

  // Categorize credentials
  const categorizeCredentials = () => {
    const categorized = {};

    Object.keys(credentialCategories).forEach((key) => {
      categorized[key] = [];
    });

    credentials.forEach((cred) => {
      const labelLower = cred.label.toLowerCase();
      let matched = false;

      for (const [catKey, cat] of Object.entries(credentialCategories)) {
        if (catKey === "other") continue;

        // Check if label starts with category name (e.g., "CMS Custom Field" -> cms category)
        if (
          labelLower.startsWith(catKey + " ") ||
          labelLower.startsWith(cat.name.toLowerCase() + " ")
        ) {
          categorized[catKey].push(cred);
          matched = true;
          break;
        }

        for (const field of cat.fields) {
          if (
            labelLower.includes(field.key.replace(/_/g, " ")) ||
            labelLower.includes(field.label.toLowerCase()) ||
            field.label.toLowerCase().includes(labelLower)
          ) {
            categorized[catKey].push(cred);
            matched = true;
            break;
          }
        }
        if (matched) break;
      }

      if (!matched) {
        categorized.other.push(cred);
      }
    });

    return categorized;
  };

  // Copy to clipboard
  const handleCopy = async (text, fieldId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldId);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      showToast("Failed to copy", "error");
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = (fieldId) => {
    setVisiblePasswords((prev) => ({
      ...prev,
      [fieldId]: !prev[fieldId],
    }));
  };

  // Toggle category expansion
  const toggleCategory = (catKey) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [catKey]: !prev[catKey],
    }));
  };

  // Get color classes - distinct professional colors for each category
  const getColorClasses = (color) => {
    const colors = {
      blue: {
        bg: "bg-blue-100 dark:bg-blue-900/30",
        icon: "bg-gradient-to-br from-blue-500 to-blue-600",
        text: "text-blue-700 dark:text-blue-400",
        border: "border-blue-200 dark:border-blue-800",
      },
      green: {
        bg: "bg-emerald-100 dark:bg-emerald-900/30",
        icon: "bg-gradient-to-br from-emerald-500 to-emerald-600",
        text: "text-emerald-700 dark:text-emerald-400",
        border: "border-emerald-200 dark:border-emerald-800",
      },
      purple: {
        bg: "bg-violet-100 dark:bg-violet-900/30",
        icon: "bg-gradient-to-br from-violet-500 to-violet-600",
        text: "text-violet-700 dark:text-violet-400",
        border: "border-violet-200 dark:border-violet-800",
      },
      orange: {
        bg: "bg-amber-100 dark:bg-amber-900/30",
        icon: "bg-gradient-to-br from-amber-500 to-orange-500",
        text: "text-amber-700 dark:text-amber-400",
        border: "border-amber-200 dark:border-amber-800",
      },
      cyan: {
        bg: "bg-cyan-100 dark:bg-cyan-900/30",
        icon: "bg-gradient-to-br from-cyan-500 to-teal-500",
        text: "text-cyan-700 dark:text-cyan-400",
        border: "border-cyan-200 dark:border-cyan-800",
      },
      emerald: {
        bg: "bg-teal-100 dark:bg-teal-900/30",
        icon: "bg-gradient-to-br from-teal-500 to-green-500",
        text: "text-teal-700 dark:text-teal-400",
        border: "border-teal-200 dark:border-teal-800",
      },
      gray: {
        bg: "bg-slate-100 dark:bg-slate-700/50",
        icon: "bg-gradient-to-br from-slate-500 to-slate-600",
        text: "text-slate-700 dark:text-slate-300",
        border: "border-slate-200 dark:border-slate-600",
      },
    };
    return colors[color] || colors.gray;
  };

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: "danger",
    title: "",
    message: "",
    onConfirm: null,
    confirmText: "Confirm",
  });

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    clientName: "",
    projectStatus: "new",
    projectType: "",
    category: "",
    startDate: "",
    endDate: "",
    liveSiteUrl: "",
    demoSiteUrls: [""],
    markupUrl: "",
  });

  // Helper to format date as YYYY-MM-DD for input[type="date"]
  function formatDate(date) {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d)) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  // Fetch project activities
  const fetchActivities = async () => {
    if (!project?._id) return;

    try {
      setLoadingActivities(true);
      const response = await activityAPI.getProjectActivities(
        project._id,
        1,
        50
      );
      setActivities(response.data.activities || []);
    } catch (error) {
      console.error("Error fetching activities:", error);
      showToast("Failed to load project activities", "error");
    } finally {
      setLoadingActivities(false);
    }
  };

  // Format activity timestamp
  const formatActivityTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoryAPI.getCategories();
        setCategories(response.data || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (project) {
      // Handle backward compatibility: convert old single demoSiteUrl to array
      let demoSiteUrls = [""];
      if (project.demoSiteUrls && Array.isArray(project.demoSiteUrls)) {
        // If array exists but is empty, use one empty string; otherwise use the array
        demoSiteUrls =
          project.demoSiteUrls.length > 0 ? project.demoSiteUrls : [""];
      } else if (project.demoSiteUrl) {
        // Backward compatibility: convert old single URL to array
        demoSiteUrls = [project.demoSiteUrl];
      }

      setFormData({
        name: project.name || "",
        description: project.description || "",
        clientName: project.clientName || "",
        projectStatus: project.projectStatus || "new",
        projectType: project.projectType || "maintenance",
        category: project.category?._id || project.category || "",
        startDate: formatDate(project.startDate),
        endDate: formatDate(project.endDate),
        liveSiteUrl: project.liveSiteUrl || "",
        demoSiteUrls: demoSiteUrls,
        markupUrl: project.markupUrl || "",
      });

      // Set uploaded files from project attachments
      if (project.attachments && project.attachments.length > 0) {
        setUploadedFiles(
          project.attachments.map((attachment) => ({
            id: attachment._id || Date.now() + Math.random(),
            name: attachment.originalName,
            size: attachment.size,
            type: attachment.mimeType,
            url: attachment.url,
            attachment: attachment,
          }))
        );
      }

      // Set credentials from project
      if (project.credentials) {
        setCredentials(project.credentials);
        // Initialize local values from credentials
        const initialValues = {};
        project.credentials.forEach((cred) => {
          initialValues[cred.label.toLowerCase()] = cred.value || "";
        });
        setLocalValues(initialValues);
      }
      // Set credential access list
      if (project.credentialAccess) {
        setCredentialAccess(project.credentialAccess);
      }
      // Set descriptions from project
      if (project.descriptions) {
        setDescriptions(project.descriptions);
      }
    }
  }, [project]);

  // Join project room for real-time updates
  useEffect(() => {
    if (project?._id) {
      joinProject(project._id);
    }

    // Leave project room on unmount or project change
    return () => {
      if (project?._id) {
        leaveProject(project._id);
      }
    };
  }, [project?._id, joinProject, leaveProject]);

  // Handle click outside to close modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if confirmation modal is open
      if (confirmModal.isOpen) {
        return;
      }
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose, confirmModal.isOpen]);

  // Fetch activities when component mounts
  useEffect(() => {
    fetchActivities();
  }, [project]);

  // Listen for real-time credential access events
  useEffect(() => {
    if (!socket || !project?._id) return;

    // Handle credential access granted (for the specific user)
    const handleCredentialAccessGranted = (data) => {
      if (data.projectId === project._id) {
        // Update credentials and access list
        if (data.project?.credentials) {
          setCredentials(data.project.credentials);
        }
        if (data.project?.credentialAccess) {
          setCredentialAccess(data.project.credentialAccess);
        }
        showToast("You have been granted credential access", "success");
      }
    };

    // Handle credential access revoked (for the specific user)
    const handleCredentialAccessRevoked = (data) => {
      if (data.projectId === project._id) {
        // Clear credentials and switch to info tab
        setCredentials([]);
        setCredentialAccess([]);
        setActiveTab("info");
        showToast("Your credential access has been revoked", "warning");
      }
    };

    // Handle project-wide credential access updates (for all viewers)
    const handleProjectCredentialAccessUpdated = (data) => {
      if (data.projectId === project._id) {
        // Update credential access list for all viewers
        if (data.credentialAccess) {
          setCredentialAccess(data.credentialAccess);
        }

        // Show toast notification to admins and other viewers
        const actionText =
          data.action === "granted" ? "granted to" : "revoked from";
        showToast(
          `Credential access ${actionText} ${data.memberName}`,
          data.action === "granted" ? "info" : "warning"
        );
      }
    };

    socket.on("credential-access-granted", handleCredentialAccessGranted);
    socket.on("credential-access-revoked", handleCredentialAccessRevoked);
    socket.on(
      "project-credential-access-updated",
      handleProjectCredentialAccessUpdated
    );

    return () => {
      socket.off("credential-access-granted", handleCredentialAccessGranted);
      socket.off("credential-access-revoked", handleCredentialAccessRevoked);
      socket.off(
        "project-credential-access-updated",
        handleProjectCredentialAccessUpdated
      );
    };
  }, [socket, project?._id, showToast]);

  const handleSubmit = async (e) => {
    // Prevent default form behavior
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }

    if (!formData.name.trim()) {
      showToast("Project name is required", "error");
      return;
    }

    if (!formData.projectType) {
      showToast("Project type is required", "error");
      return;
    }

    if (!formData.startDate) {
      showToast("Start date is required", "error");
      return;
    }

    setLoading(true);
    try {
      // First update the project without new attachments
      const existingAttachments = uploadedFiles
        .filter((file) => file.attachment) // Keep existing attachments
        .map((file) => file.attachment);

      const updateData = {
        ...formData,
        // Filter out empty demo site URLs before sending
        demoSiteUrls: formData.demoSiteUrls.filter((url) => url.trim() !== ""),
        attachments: existingAttachments,
      };
      await updateProject(project._id, updateData);

      // Upload new files if any
      const newFiles = uploadedFiles.filter(
        (file) => file.file && !file.attachment
      );
      if (newFiles.length > 0) {
        const uploadFormData = new FormData();
        newFiles.forEach((fileObj, index) => {
          if (fileObj.file) {
            uploadFormData.append(`file${index}`, fileObj.file);
          }
        });

        try {
          console.log("Uploading files to project:", project._id);
          const response = await projectAPI.uploadFiles(
            project._id,
            uploadFormData
          );

          console.log("File upload successful:", response.data);

          // Update the uploadedFiles state to show the uploaded files
          const uploadedFileObjects = uploadedFiles.map((fileObj, index) => ({
            id: `uploaded-${Date.now()}-${index}`,
            name: fileObj.name,
            size: fileObj.size,
            type: fileObj.type,
            url: response.data.attachments[index]?.url || fileObj.url,
            uploaded: true,
          }));

          setUploadedFiles(uploadedFileObjects);

          showToast(
            `${uploadedFiles.length} file(s) uploaded successfully!`,
            "success"
          );
        } catch (uploadError) {
          console.error("File upload error:", uploadError);
          showToast(
            "Project updated but some files failed to upload",
            "warning"
          );
        }
      }

      showToast("Project updated successfully!", "success");

      // Refresh activities after successful update
      await fetchActivities();

      // Don't auto-close the modal - let user close it manually
      // onClose();
    } catch (error) {
      console.error("Edit project error:", error);
      showToast(error?.response?.data?.message || "Failed to update project", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      // If projectType changes and is not "ongoing", clear endDate
      if (name === "projectType" && value !== "ongoing") {
        return {
          ...prev,
          [name]: value,
          endDate: "",
        };
      }
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  // Handlers for demo site URLs array
  const addDemoSiteUrl = () => {
    setFormData((prev) => ({
      ...prev,
      demoSiteUrls: [...prev.demoSiteUrls, ""],
    }));
  };

  const removeDemoSiteUrl = (index) => {
    setFormData((prev) => ({
      ...prev,
      demoSiteUrls: prev.demoSiteUrls.filter((_, i) => i !== index),
    }));
  };

  const handleDemoSiteUrlChange = (index, value) => {
    setFormData((prev) => ({
      ...prev,
      demoSiteUrls: prev.demoSiteUrls.map((url, i) =>
        i === index ? value : url
      ),
    }));
  };

  // File upload handlers (same as CreateProjectModal)
  const handleFileUpload = async (files) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray.filter((file) => {
      const maxSize = 25 * 1024 * 1024; // 25MB
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/plain",
        "application/json",
      ];

      if (file.size > maxSize) {
        showToast(
          `File ${file.name} is too large. Maximum size is 25MB.`,
          "error"
        );
        return false;
      }

      if (!allowedTypes.includes(file.type)) {
        showToast(`File type ${file.type} is not supported.`, "error");
        return false;
      }

      return true;
    });

    if (validFiles.length === 0) return;

    // Create temporary file objects for preview
    const newFiles = validFiles.map((file) => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      url: URL.createObjectURL(file), // Temporary URL for preview
      file: file, // Store actual file for upload
      uploading: false,
      uploaded: false,
    }));

    setUploadedFiles((prev) => [...prev, ...newFiles]);
    showToast(`${validFiles.length} file(s) added successfully`, "success");
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    handleFileUpload(files);
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    handleFileUpload(files);
    e.target.value = ""; // Reset input
  };

  const removeFile = async (fileId) => {
    const fileToRemove = uploadedFiles.find((file) => file.id === fileId);

    setConfirmModal({
      isOpen: true,
      type: "danger",
      title: "Delete File",
      message: `Are you sure you want to delete "${
        fileToRemove?.name || "this file"
      }"? This action cannot be undone.`,
      confirmText: "Delete",
      onConfirm: async () => {
        setLoading(true);
        try {
          // If it's an existing attachment, delete it from server
          if (fileToRemove && fileToRemove.attachment) {
            await projectAPI.deleteAttachment(
              project._id,
              fileToRemove.attachment._id
            );
          }

          setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId));
          showToast("File deleted successfully", "success");
        } catch (error) {
          console.error("Delete attachment error:", error);
          showToast("Failed to delete file", "error");
        } finally {
          setLoading(false);
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  const openFilePreview = (file) => {
    window.open(file.url, "_blank");
  };

  // New handlers for revealing data
  const handleRevealCredential = async (credentialId) => {
    const credential = credentials.find((c) => c._id === credentialId);
    if (!credential) return;

    if (credential.isRevealed) {
      // Hide data
      setLocalValues((prev) => ({
        ...prev,
        [credential.label.toLowerCase()]: "********",
      }));
      setCredentials((prev) =>
        prev.map((c) =>
          c._id === credentialId
            ? { ...c, value: "********", isRevealed: false }
            : c
        )
      );
      return;
    }

    try {
      const response = await projectAPI.revealCredential(
        project._id,
        credentialId
      );
      if (response.data.success) {
        const revealedValue = response.data.value;
        // Update local values and also the credentials list to show plain text
        setLocalValues((prev) => ({
          ...prev,
          [credential.label.toLowerCase()]: revealedValue,
        }));
        setCredentials((prev) =>
          prev.map((c) =>
            c._id === credentialId
              ? { ...c, value: revealedValue, isRevealed: true }
              : c
          )
        );
      }
    } catch (error) {
      showToast("Failed to reveal credential", "error");
    }
  };

  // Credentials Handlers
  const handleAddCredential = async (credentialData = null) => {
    const dataToAdd = credentialData || newCredential;

    if (!dataToAdd.label?.trim()) {
      showToast("Label is required", "error");
      return;
    }

    const credKey = dataToAdd.label.toLowerCase();
    setSavingCredentialKey(credKey);
    try {
      let finalData = { ...dataToAdd };
      if (rsaPublicKey && finalData.value) {
        try {
          const encrypted = await encryptForBackend(
            finalData.value,
            rsaPublicKey
          );
          finalData = {
            ...finalData,
            ...encrypted,
            isEncrypted: true,
            value: "********",
          };
        } catch (encError) {
          console.error("Encryption failed, sending plain text:", encError);
        }
      }

      const response = await projectAPI.addCredential(project._id, finalData);
      if (response.data.success) {
        setCredentials(response.data.project.credentials);
        setCredentialAccess(response.data.project.credentialAccess || []);
        // Update localValues with the new credential
        setLocalValues((prev) => ({
          ...prev,
          [credKey]: dataToAdd.value || "",
        }));
        setNewCredential({ label: "", value: "" });
        showToast("Credential added successfully", "success");
      }
    } catch (error) {
      console.error("Add credential error:", error);
      showToast(
        error.response?.data?.message || "Failed to add credential",
        "error"
      );
    } finally {
      setSavingCredentialKey(null);
    }
  };

  const handleUpdateCredential = async (credentialId, updates) => {
    const credKey = updates.label?.toLowerCase();
    setSavingCredentialKey(credKey);
    try {
      let finalUpdates = { ...updates };
      
      // If value is still masked, don't send it to the server
      if (finalUpdates.value === "********") {
        delete finalUpdates.value;
      } else if (rsaPublicKey && finalUpdates.value) {
        // Encrypt new value
        try {
          const encrypted = await encryptForBackend(
            finalUpdates.value,
            rsaPublicKey
          );
          finalUpdates = {
            ...finalUpdates,
            ...encrypted,
            isEncrypted: true,
            value: "********",
          };
        } catch (encError) {
          console.error("Encryption failed, sending plain text:", encError);
        }
      }

      // Only send if there's something to update
      if (Object.keys(finalUpdates).length > 0) {
        const response = await projectAPI.updateCredential(
          project._id,
          credentialId,
          finalUpdates
        );
        if (response.data.success) {
          setCredentials(response.data.project.credentials);
          showToast("Credential updated successfully", "success");
        }
      } else {
        showToast("No changes to save", "info");
      }
    } catch (error) {
      console.error("Update credential error:", error);
      showToast(
        error.response?.data?.message || "Failed to update credential",
        "error"
      );
    } finally {
      setSavingCredentialKey(null);
    }
  };

  const handleDeleteCredential = (credentialId) => {
    const credential = credentials.find((c) => c._id === credentialId);
    setConfirmModal({
      isOpen: true,
      type: "danger",
      title: "Delete Credential",
      message: `Are you sure you want to delete the credential "${
        credential?.label || "this credential"
      }"? This action cannot be undone.`,
      confirmText: "Delete",
      onConfirm: async () => {
        setCredentialLoading(true);
        try {
          const response = await projectAPI.deleteCredential(
            project._id,
            credentialId
          );
          if (response.data.success) {
            setCredentials(response.data.project.credentials);
            // Remove deleted credential from localValues
            if (credential?.label) {
              setLocalValues((prev) => {
                const newValues = { ...prev };
                delete newValues[credential.label.toLowerCase()];
                return newValues;
              });
            }
            showToast("Credential deleted successfully", "success");
          }
        } catch (error) {
          console.error("Delete credential error:", error);
          showToast(
            error.response?.data?.message || "Failed to delete credential",
            "error"
          );
        } finally {
          setCredentialLoading(false);
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  // Credential Access Handlers
  const handleGrantAccess = async (memberId) => {
    setCredentialLoading(true);
    try {
      const response = await projectAPI.grantCredentialAccess(
        project._id,
        memberId
      );
      if (response.data.success) {
        setCredentialAccess(response.data.project.credentialAccess);
        showToast(response.data.message, "success");
      }
    } catch (error) {
      console.error("Grant access error:", error);
      showToast(
        error.response?.data?.message || "Failed to grant access",
        "error"
      );
    } finally {
      setCredentialLoading(false);
    }
  };

  const handleRevokeAccess = (memberId) => {
    // Find user details for the confirmation message
    const userDetails = getCredentialAccessUserDetails(
      credentialAccess.find((ca) => (ca.user?._id || ca.user) === memberId)
    );

    setConfirmModal({
      isOpen: true,
      type: "danger",
      title: "Revoke Credential Access",
      message: `Are you sure you want to revoke credential access for "${
        userDetails?.name || "this member"
      }"? They will no longer be able to view project credentials.`,
      confirmText: "Revoke Access",
      onConfirm: async () => {
        setCredentialLoading(true);
        try {
          const response = await projectAPI.revokeCredentialAccess(
            project._id,
            memberId
          );
          if (response.data.success) {
            setCredentialAccess(response.data.project.credentialAccess);
            showToast(response.data.message, "success");
          }
        } catch (error) {
          console.error("Revoke access error:", error);
          showToast(
            error.response?.data?.message || "Failed to revoke access",
            "error"
          );
        } finally {
          setCredentialLoading(false);
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  // Helper to close confirmation modal
  const closeConfirmModal = () => {
    if (!credentialLoading) {
      setConfirmModal((prev) => ({ ...prev, isOpen: false }));
    }
  };

  // Description Handlers
  const handleAddDescription = async () => {
    if (!newDescription.trim()) {
      showToast("Description content is required", "error");
      return;
    }

    setDescriptionLoading(true);
    try {
      let finalContent = newDescription;
      let payload = { content: finalContent };

      if (rsaPublicKey) {
        try {
          const encrypted = await encryptForBackend(finalContent, rsaPublicKey);
          payload = {
            ...payload,
            ...encrypted,
            isEncrypted: true,
            content: "********",
          };
        } catch (encError) {
          console.error("Encryption failed, sending plain text:", encError);
        }
      }

      const response = await projectAPI.addDescription(project._id, payload);
      if (response.data.success) {
        setDescriptions(response.data.project.descriptions);
        setNewDescription("");
        showToast("Description added successfully", "success");
      }
    } catch (error) {
      console.error("Add description error:", error);
      showToast(
        error.response?.data?.message || "Failed to add description",
        "error"
      );
    } finally {
      setDescriptionLoading(false);
    }
  };

  const handleUpdateDescription = async (descriptionId) => {
    if (!editingDescriptionContent.trim()) {
      showToast("Description content is required", "error");
      return;
    }

    setDescriptionLoading(true);
    try {
      let finalContent = editingDescriptionContent;
      let payload = { content: finalContent };

      if (rsaPublicKey && finalContent !== "********") {
        try {
          const encrypted = await encryptForBackend(finalContent, rsaPublicKey);
          payload = {
            ...payload,
            ...encrypted,
            isEncrypted: true,
            content: "********",
          };
        } catch (encError) {
          console.error("Encryption failed, sending plain text:", encError);
        }
      }

      const response = await projectAPI.updateDescription(
        project._id,
        descriptionId,
        payload
      );
      if (response.data.success) {
        setDescriptions(response.data.project.descriptions);
        setEditingDescriptionId(null);
        setEditingDescriptionContent("");
        showToast("Description updated successfully", "success");
      }
    } catch (error) {
      console.error("Update description error:", error);
      showToast(
        error.response?.data?.message || "Failed to update description",
        "error"
      );
    } finally {
      setDescriptionLoading(false);
    }
  };

  const handleDeleteDescription = (descriptionId) => {
    setConfirmModal({
      isOpen: true,
      type: "danger",
      title: "Delete Description",
      message:
        "Are you sure you want to delete this description? This action cannot be undone.",
      confirmText: "Delete",
      onConfirm: async () => {
        setDescriptionLoading(true);
        try {
          const response = await projectAPI.deleteDescription(
            project._id,
            descriptionId
          );
          if (response.data.success) {
            setDescriptions(response.data.project.descriptions);
            showToast("Description deleted successfully", "success");
          }
        } catch (error) {
          console.error("Delete description error:", error);
          showToast(
            error.response?.data?.message || "Failed to delete description",
            "error"
          );
        } finally {
          setDescriptionLoading(false);
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  const startEditingDescription = (desc) => {
    setEditingDescriptionId(desc._id);
    setEditingDescriptionContent(desc.content);
  };

  const cancelEditingDescription = () => {
    setEditingDescriptionId(null);
    setEditingDescriptionContent("");
  };

  // Helper function to get user details from credentialAccess
  // The user might be populated (object) or just an ID (string)
  const getCredentialAccessUserDetails = (access) => {
    if (!access) return { name: "Unknown User", email: "", color: "#6B7280" };

    // If user is already populated with details
    if (access.user && typeof access.user === "object" && access.user.name) {
      return access.user;
    }

    // Get user ID (could be object with _id or just string)
    const userId = access.user?._id || access.user;
    const userIdStr = userId?.toString ? userId.toString() : userId;

    // Look up user from project members
    const member = project?.members?.find((m) => {
      if (!m.user) return false;
      const memberId = m.user._id?.toString
        ? m.user._id.toString()
        : m.user._id;
      return memberId === userIdStr;
    });

    if (member?.user) {
      return member.user;
    }

    return { _id: userId, name: "Unknown User", email: "", color: "#6B7280" };
  };

  // Check if current user has credential access
  const hasCredentialAccess =
    isAdmin ||
    credentialAccess.some(
      (ca) => ca.user && (ca.user._id === user?._id || ca.user === user?._id)
    );

  // Get members without credential access (for granting)
  const membersWithoutAccess =
    project?.members?.filter(
      (member) =>
        member.user &&
        member.user.role !== "admin" &&
        !credentialAccess.some((ca) => {
          const caUserId = ca.user?._id || ca.user;
          return (
            caUserId &&
            (caUserId === member.user._id ||
              caUserId?.toString() === member.user._id?.toString())
          );
        })
    ) || [];

  const getNoteAuthor = (desc) => {
    if (desc?.createdBy && typeof desc.createdBy === "object" && desc.createdBy.name) {
      return desc.createdBy;
    }
    const creatorId = (desc?.createdBy?._id || desc?.createdBy?.id || desc?.createdBy)?.toString();
    if (creatorId) {
      if (user && (user._id?.toString() === creatorId || user.id?.toString() === creatorId)) {
        return user;
      }
      const member = (project?.members || []).find(
        (m) => (m.user?._id?.toString() || m.user?.id?.toString() || m.user?.toString()) === creatorId
      );
      if (member && typeof member.user === "object" && member.user?.name) {
        return member.user;
      }
    }
    return desc?.createdBy || { name: "Unknown" };
  };

  if (!project) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl max-w-7xl w-full mx-4 max-h-[90vh] overflow-hidden relative flex flex-col transition-colors duration-200"
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 h-[61.19px] min-h-[61.19px] max-h-[61.19px] bg-[#f1f2f4] dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 shrink-0 box-border rounded-t-lg transition-colors duration-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-500/10 dark:bg-blue-500/20 rounded-lg flex items-center justify-center text-blue-600 dark:text-blue-400 flex-shrink-0">
              <Save className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white leading-tight">
                Project Details
              </h2>
              <p className="text-xs text-gray-500 dark:text-slate-400">
                Manage project information and credentials
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                type="button"
                onClick={() => setEditMode(!editMode)}
                className="h-8 px-3 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-1.5"
                title="Toggle Edit Mode"
              >
                <Pencil className="w-3.5 h-3.5" />
                <span>{!editMode ? "Edit mode" : "Cancel edit"}</span>
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 min-w-[32px] min-h-[32px] max-w-[32px] max-h-[32px] flex items-center justify-center rounded-lg hover:bg-gray-400/20 transition-colors text-gray-600 dark:text-slate-400"
              title="Close modal"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="flex border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex-shrink-0 transition-colors duration-200">
          <button
            type="button"
            onClick={() => setActiveTab("info")}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "info"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 hover:border-gray-300 dark:hover:border-slate-600"
            }`}
          >
            <Info className="w-4 h-4" />
            Project Information
          </button>
          {(isAdmin || hasCredentialAccess) && (
            <button
              type="button"
              onClick={() => setActiveTab("credentials")}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "credentials"
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 hover:border-gray-300 dark:hover:border-slate-600"
              }`}
            >
              <Key className="w-4 h-4" />
              Credentials
              {isAdmin && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded">
                  {credentials.length}
                </span>
              )}
            </button>
          )}
          <button
            type="button"
            onClick={() => setActiveTab("descriptions")}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "descriptions"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 hover:border-gray-300 dark:hover:border-slate-600"
            }`}
          >
            <FileText className="w-4 h-4" />
            Notes
            <span className="ml-1 px-1.5 py-0.5 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded">
              {descriptions.length}
            </span>
          </button>
        </div>

        {/* Modal Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4">
            {/* PROJECT INFORMATION TAB */}
            {activeTab === "info" && (
              <>
                <form
                  id="edit-project-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    // Don't submit the form automatically - only on Save button click
                    return false;
                  }}
                  className="space-y-8"
                >
                  {/* Section 1: Project Information */}
                  <div className="bg-gray-50 dark:bg-slate-700/30 rounded-lg p-4 border border-gray-200 dark:border-slate-700 transition-colors duration-200">
                    <div className="flex items-center mb-4">
                      <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center mr-3">
                        <Save className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-base font-semibold dark:text-white">
                        Project Information
                      </h3>
                    </div>

                    <div className="space-y-4">
                      {/* First Row: Project Name, Client Name, Project Status */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Project Name */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                            Project Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className="w-full h-8 px-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-xs transition-all duration-200"
                            placeholder="Enter project name"
                            required
                            disabled={editMode}
                          />
                        </div>
                        {/* Client Name */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                            Client Name
                          </label>
                          <input
                            type="text"
                            name="clientName"
                            value={formData.clientName}
                            onChange={handleChange}
                            className="w-full h-8 px-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-xs transition-all duration-200"
                            placeholder="Enter client name"
                            disabled={editMode}
                          />
                        </div>
                        {/* Project Status */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                            Project Status{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <CustomStatusDropdown
                            value={formData.projectStatus}
                            onChange={(value) => setFormData((prev) => ({ ...prev, projectStatus: value }))}
                            options={[
                              { value: "new", label: "New" },
                              { value: "ongoing", label: "Ongoing" },
                              { value: "completed", label: "Completed" },
                              { value: "cancelled", label: "Cancelled" },
                            ]}
                            disabled={editMode}
                          />
                        </div>
                      </div>

                      {/* Second Row: Project Type, Start Date, End Date */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Project Type */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                            Project Type <span className="text-red-500">*</span>
                          </label>
                          <CustomStatusDropdown
                            value={formData.projectType}
                            onChange={(value) => {
                              setFormData((prev) => ({
                                ...prev,
                                projectType: value,
                                // Clear endDate if projectType is not "ongoing"
                                endDate: value !== "ongoing" ? "" : prev.endDate,
                              }));
                            }}
                            options={[
                              { value: "maintenance", label: "Maintenance" },
                              { value: "ongoing", label: "Ongoing" },
                              { value: "one-time", label: "One Time" },
                            ]}
                            disabled={editMode}
                          />
                        </div>
                        {/* Start Date */}
                        <div className="relative">
                          <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                            Start Date <span className="text-red-500">*</span>
                          </label>
                          <button
                            type="button"
                            onClick={() => setShowStartDatePicker(!showStartDatePicker)}
                            disabled={editMode}
                            className="w-full h-8 px-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-xs transition-all duration-200 flex items-center justify-between hover:border-gray-400 dark:hover:border-slate-500 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <span className={formData.startDate ? "text-black dark:text-white" : "text-gray-400"}>
                              {formData.startDate ? new Date(formData.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : "Select start date"}
                            </span>
                            <Calendar className="w-4 h-4 text-gray-400" />
                          </button>
                          {showStartDatePicker && !editMode && (
                            <div ref={startDatePickerRef} className="relative">
                              <CustomDatePicker
                                value={formData.startDate}
                                onChange={(date) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    startDate: date,
                                  }));
                                }}
                                onClose={() => setShowStartDatePicker(false)}
                              />
                            </div>
                          )}
                        </div>
                        {/* End Date */}
                        <div className="relative">
                          <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                            End Date
                          </label>
                          <button
                            type="button"
                            onClick={() => setShowEndDatePicker(!showEndDatePicker)}
                            disabled={editMode}
                            className="w-full h-8 px-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-xs transition-all duration-200 flex items-center justify-between hover:border-gray-400 dark:hover:border-slate-500 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <span className={formData.endDate ? "text-black dark:text-white" : "text-gray-400"}>
                              {formData.endDate ? new Date(formData.endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : "Select end date"}
                            </span>
                            <Calendar className="w-4 h-4 text-gray-400" />
                          </button>
                          {showEndDatePicker && !editMode && (
                            <div ref={endDatePickerRef} className="relative">
                              <CustomDatePicker
                                value={formData.endDate}
                                onChange={(date) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    endDate: date,
                                  }));
                                }}
                                onClose={() => setShowEndDatePicker(false)}
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Category */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                          Category
                        </label>
                        <CustomStatusDropdown
                          value={formData.category}
                          onChange={(value) => setFormData((prev) => ({ ...prev, category: value }))}
                          options={[
                            { value: "", label: "No Category" },
                            ...categories.map((cat) => ({
                              value: cat._id,
                              label: cat.name,
                            })),
                          ]}
                          disabled={editMode}
                        />
                      </div>

                      {/* Description */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                          Description
                        </label>
                        <div className="border border-gray-300 dark:border-slate-700 rounded-lg overflow-hidden">
                          {!editMode ? (
                            <SimpleQuillEditor
                              value={formData.description}
                              onChange={(content) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  description: content,
                                }))
                              }
                              placeholder="Enter project description"
                              height="200px"
                              readOnly={editMode}
                            />
                          ) : (
                            <div className="bg-white dark:bg-slate-900 px-3 py-2 min-h-[32px] flex items-center">
                              <div
                                className="dark:text-slate-200 text-xs leading-relaxed"
                                dangerouslySetInnerHTML={{
                                  __html: formData.description || "<span class='text-gray-400'>No description provided</span>",
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Site URLs */}
                  <div className="bg-gray-50 dark:bg-slate-700/30 rounded-lg p-4 border border-gray-200 dark:border-slate-700 transition-colors duration-200">
                    <div className="flex items-center mb-4">
                      <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                        <Link className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-base font-semibold dark:text-white">
                        Site URLs
                      </h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Live Site URL */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                          Live Site URL
                        </label>
                        <input
                          type="url"
                          name="liveSiteUrl"
                          value={formData.liveSiteUrl}
                          onChange={handleChange}
                          className="w-full h-8 px-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-xs transition-all duration-200"
                          placeholder="https://example.com"
                          disabled={editMode}
                        />
                      </div>
                      {/* Demo Site URL */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300">
                            Demo Site URL
                          </label>
                        </div>
                        <div className="space-y-2">
                          {formData.demoSiteUrls.map((url, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-1.5"
                            >
                              <div className="relative flex-1 flex items-center">
                                <input
                                  type="url"
                                  value={url}
                                  onChange={(e) =>
                                    handleDemoSiteUrlChange(index, e.target.value)
                                  }
                                  className={`w-full h-8 pl-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-xs transition-all duration-200 ${
                                    index === 0 && !editMode ? "pr-8" : "pr-3"
                                  }`}
                                  placeholder="https://demo.example.com"
                                  disabled={editMode}
                                />
                                {index === 0 && !editMode && (
                                  <button
                                    type="button"
                                    onClick={addDemoSiteUrl}
                                    className="absolute right-1 w-6 h-6 flex items-center justify-center bg-green-500 hover:bg-green-600 text-white rounded-md transition-all duration-200 hover:scale-105"
                                    title="Add demo site URL"
                                  >
                                    <Plus className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                              {!editMode &&
                                formData.demoSiteUrls.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => removeDemoSiteUrl(index)}
                                    className="w-8 h-8 min-w-[32px] min-h-[32px] flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200"
                                    title="Remove this URL"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                            </div>
                          ))}
                        </div>
                      </div>
                      {/* Markup URL */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                          Markup URL
                        </label>
                        <input
                          type="url"
                          name="markupUrl"
                          value={formData.markupUrl}
                          onChange={handleChange}
                          className="w-full h-8 px-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-xs transition-all duration-200"
                          placeholder="https://markup.example.com"
                          disabled={editMode}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Files and Documents */}
                  <div
                    className="bg-gray-50 dark:bg-slate-700/30 rounded-lg p-4 border border-gray-200 dark:border-slate-700 transition-colors duration-200"
                    style={{
                      cursor: editMode && isAdmin ? "not-allowed" : "default",
                    }}
                  >
                    <div className="flex items-center mb-4">
                      <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                        <Upload className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-base font-semibold dark:text-white">
                        Files and Documents
                      </h3>
                    </div>

                    {/* Drag and Drop Area */}
                    {isAdmin && (
                      <div
                        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors duration-200 ${
                          isDragOver
                            ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                            : "border-gray-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500"
                        }`}
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        <Upload className="w-8 h-8 mx-auto text-gray-400 dark:text-slate-500 mb-2" />
                        <p className="text-gray-600 dark:text-slate-400 mb-2">
                          Drag and drop files here, or{" "}
                          <button
                            type="button"
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                          >
                            browse files
                          </button>
                        </p>
                        <p className="text-sm text-gray-500 dark:text-slate-500">
                          Supports images, PDFs, documents (max 25MB each)
                        </p>
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          onChange={handleFileInputChange}
                          className="hidden"
                          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.json"
                          disabled={editMode}
                        />
                      </div>
                    )}

                    {/* Uploaded Files List */}
                    {uploadedFiles.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-xs font-semibold text-gray-700 dark:text-slate-300 mb-2">
                          Uploaded Files ({uploadedFiles.length})
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {uploadedFiles.map((file) => (
                            <div
                              key={file.id}
                              onClick={() => openFilePreview(file)}
                              className={`flex items-center justify-between h-[38px] px-3 rounded-lg border cursor-pointer transition-colors ${
                                file.uploaded
                                  ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                                  : "bg-gray-50 dark:bg-slate-700/50 border-gray-200 dark:border-slate-600"
                              }`}
                            >
                              <div className="flex items-center space-x-2 flex-1 min-w-0">
                                <div className="flex-shrink-0">
                                  <div className="w-6 h-6 bg-gray-100 dark:bg-slate-800 rounded flex items-center justify-center">
                                    {React.createElement(
                                      getFileIcon(file.type, file.name),
                                      {
                                        className: `w-4 h-4 ${getFileIconColor(
                                          file.type,
                                          file.name
                                        )}`,
                                      }
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                                    {file.name}
                                  </p>
                                  <span className="text-[10px] text-gray-500 dark:text-slate-400">
                                    ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center space-x-1">
                                {file.uploaded && (
                                  <div
                                    className="p-0.5 text-green-600"
                                    title="Successfully uploaded"
                                  >
                                    <svg
                                      className="w-4 h-4"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openFilePreview(file);
                                  }}
                                  className="w-7 h-7 flex items-center justify-center text-gray-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg transition-colors hover:bg-gray-200/50 dark:hover:bg-slate-700"
                                  title="View file"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeFile(file.id);
                                  }}
                                  className="w-7 h-7 flex items-center justify-center text-gray-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 rounded-lg transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
                                  title="Remove file"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </form>

                {/* Activity Log Section */}
                <div className="mt-4">
                  <div className="bg-gray-50 dark:bg-slate-700/30 rounded-lg h-[38px] px-3 border border-gray-200 dark:border-slate-700 flex items-center justify-between transition-colors duration-200">
                    <div className="flex items-center">
                      <div className="w-6 h-6 bg-green-500 rounded-md flex items-center justify-center mr-2">
                        <Clock className="w-3.5 h-3.5 text-white" />
                      </div>
                      <h3 className="text-xs font-semibold dark:text-white">
                        Project Activity
                      </h3>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowActivities(!showActivities)}
                      className="h-7 px-2.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg flex items-center gap-1 transition-colors"
                    >
                      <span>{showActivities ? "Hide" : "Show"} Activity</span>
                      <MessageSquare className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {showActivities && (
                    <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 max-h-64 overflow-y-auto transition-colors duration-200">
                      {loadingActivities ? (
                        <div className="flex items-center justify-center p-8">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 dark:border-blue-400"></div>
                          <span className="ml-2 text-gray-600 dark:text-slate-400">
                            Loading activities...
                          </span>
                        </div>
                      ) : activities.length === 0 ? (
                        <div className="text-center p-8 text-gray-500 dark:text-slate-500">
                          <Clock className="w-8 h-8 mx-auto mb-2 text-gray-400 dark:text-slate-600" />
                          <p>No activities yet</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-200 dark:divide-slate-700">
                          {activities.map((activity) => (
                            <div
                              key={activity._id}
                              className="p-4 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                            >
                              <div className="flex items-start space-x-3">
                                <Avatar
                                  user={activity.user}
                                  size="sm"
                                  className="flex-shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-2">
                                    <p className="text-sm font-normal text-gray-900 dark:text-white">
                                      {activity.user?.name || "Unknown User"}
                                    </p>
                                    <span className="text-xs text-gray-500 dark:text-slate-400">
                                      {formatActivityTime(activity.createdAt)}
                                    </span>
                                  </div>
                                  <div className="text-sm font-normal text-gray-600 dark:text-slate-300 mt-1">
                                    {activity.details?.htmlMessage ? (
                                      <div
                                        dangerouslySetInnerHTML={{
                                          __html: activity.details.htmlMessage,
                                        }}
                                        className="activity-message font-normal"
                                        style={{
                                          lineHeight: "1.6",
                                          fontSize: "14px",
                                          fontWeight: "normal",
                                        }}
                                      />
                                    ) : (
                                      <p>{activity.message}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* CREDENTIALS TAB */}
            {activeTab === "credentials" &&
              (isAdmin || hasCredentialAccess) && (
                <div className="space-y-5">
                  {/* Access Notice for Members */}
                  {!isAdmin && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                      <Shield className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                      <p className="text-xs text-slate-600 dark:text-slate-300">
                        You have view access to credentials
                      </p>
                    </div>
                  )}

                  {/* Admin: Credentials Form */}
                  {isAdmin && (
                    <div className="space-y-8">
                      {Object.entries(credentialCategories)
                        .filter(([key]) => key !== "other")
                        .map(([catKey, category]) => {
                          const IconComponent = category.icon;
                          const colors = getColorClasses(category.color);

                          return (
                            <div key={catKey} className="bg-gray-50 dark:bg-slate-700/30 rounded-lg p-4 border border-gray-200 dark:border-slate-700">
                              {/* Section Header */}
                              <div className="flex items-center gap-2 mb-4">
                                <div
                                  className={`w-8 h-8 ${colors.icon} rounded-lg flex items-center justify-center`}
                                >
                                  <IconComponent className="w-4 h-4 text-white" />
                                </div>
                                <h4 className="text-base font-semibold text-slate-800 dark:text-white">
                                  {category.name}
                                </h4>
                              </div>

                              {/* Fields Grid */}
                              <div className="space-y-3">
                                {category.fields.map((field) => {
                                  const existingCred = credentials.find(
                                    (c) =>
                                      c.label.toLowerCase() ===
                                      field.label.toLowerCase()
                                  );
                                  const inputId = `cred-${field.key}`;

                                  return (
                                    <div
                                      key={field.key}
                                      className="grid grid-cols-[160px_1fr] gap-3 items-center group "
                                    >
                                      <label
                                        htmlFor={inputId}
                                        className="text-sm font-medium text-slate-600 dark:text-slate-400"
                                      >
                                        {field.label}
                                      </label>
                                      <div className="flex items-center gap-2">
                                        {(() => {
                                          const fieldKey =
                                            field.label.toLowerCase();
                                          const isSaving =
                                            savingCredentialKey === fieldKey;
                                          const isPasswordVisible =
                                            visiblePasswords[fieldKey];
                                          return (
                                            <>
                                              <div className="relative flex-1">
                                                <input
                                                  type={
                                                    (field.isPassword && !isPasswordVisible) || (existingCred?.isEncrypted && !existingCred?.isRevealed)
                                                      ? "password"
                                                      : "text"
                                                  }
                                                  id={inputId}
                                                  value={
                                                    localValues[fieldKey] ?? ""
                                                  }
                                                  onChange={(e) =>
                                                    setLocalValues((prev) => ({
                                                      ...prev,
                                                      [fieldKey]:
                                                        e.target.value,
                                                    }))
                                                  }
                                                  placeholder="Enter value..."
                                                  className={`w-full h-8 px-3 ${
                                                    field.isPassword || (existingCred?.isEncrypted && !existingCred?.isRevealed)
                                                      ? "pr-10"
                                                      : ""
                                                  } text-xs border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent placeholder:text-slate-400 dark:placeholder:text-slate-600 transition-colors`}
                                                  disabled={isSaving}
                                                />
                                                {(field.isPassword || (existingCred?.isEncrypted)) && (
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      if (existingCred?.isEncrypted) {
                                                        handleRevealCredential(existingCred._id);
                                                      } else {
                                                        togglePasswordVisibility(fieldKey);
                                                      }
                                                    }}
                                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                                                    title={existingCred?.isEncrypted ? (existingCred.isRevealed ? "Hide encrypted data" : "Reveal encrypted data") : (isPasswordVisible ? "Hide" : "Show")}
                                                  >
                                                    {existingCred?.isRevealed || (isPasswordVisible && !existingCred?.isEncrypted) ? (
                                                      <EyeOff className="w-4 h-4" />
                                                    ) : (
                                                      <Eye className="w-4 h-4" />
                                                    )}
                                                  </button>
                                                )}
                                              </div>
                                              <div className="flex items-center">
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    const value =
                                                      localValues[fieldKey] ??
                                                      "";
                                                    if (existingCred) {
                                                      handleUpdateCredential(
                                                        existingCred._id,
                                                        {
                                                          label: field.label,
                                                          value,
                                                        }
                                                      );
                                                    } else if (value.trim()) {
                                                      handleAddCredential({
                                                        label: field.label,
                                                        value,
                                                      });
                                                    }
                                                  }}
                                                  disabled={isSaving}
                                                  className="h-8 px-3 text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-blue-600 dark:hover:bg-blue-500 hover:text-white rounded-lg transition-all duration-200 disabled:opacity-50 whitespace-nowrap"
                                                >
                                                  {isSaving ? "..." : "Save"}
                                                </button>
                                                {existingCred && (
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      handleDeleteCredential(
                                                        existingCred._id
                                                      )
                                                    }
                                                    disabled={credentialLoading}
                                                    className="h-8 flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200 w-0 opacity-0 group-hover:w-8 group-hover:opacity-100 group-hover:ml-2 overflow-hidden"
                                                    title="Delete"
                                                  >
                                                    <Trash2 className="w-4 h-4 flex-shrink-0" />
                                                  </button>
                                                )}
                                              </div>
                                            </>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                  );
                                })}

                                {/* CMS Only: Display Custom CMS Fields */}
                                {catKey === "cms" &&
                                  (() => {
                                    const customCmsCredentials =
                                      categorizeCredentials().cms?.filter(
                                        (cred) =>
                                          !category.fields.some(
                                            (f) =>
                                              f.label.toLowerCase() ===
                                              cred.label.toLowerCase()
                                          )
                                      ) || [];

                                    return customCmsCredentials.map((cred) => {
                                      const credKey = cred.label.toLowerCase();
                                      const isSaving =
                                        savingCredentialKey === credKey;
                                      return (
                                        <div
                                          key={cred._id}
                                          className="grid grid-cols-[160px_1fr] gap-3 items-center group"
                                        >
                                          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                                            {cred.label}
                                          </label>
                                          <div className="flex items-center gap-2">
                                            <div className="relative flex-1">
                                              <input
                                                type={cred.isRevealed || !cred.isEncrypted ? "text" : "password"}
                                                value={localValues[credKey] ?? ""}
                                                onChange={(e) =>
                                                  setLocalValues((prev) => ({
                                                    ...prev,
                                                    [credKey]: e.target.value,
                                                  }))
                                                }
                                                placeholder="Enter value..."
                                                className={`w-full h-8 px-3 ${cred.isEncrypted ? "pr-10" : ""} text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent placeholder:text-slate-400 dark:placeholder:text-slate-600 transition-colors`}
                                                disabled={isSaving}
                                              />
                                              {cred.isEncrypted && (
                                                <button
                                                  type="button"
                                                  onClick={() => handleRevealCredential(cred._id)}
                                                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                                                  title={cred.isRevealed ? "Hide encrypted data" : "Reveal encrypted data"}
                                                >
                                                  {cred.isRevealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                </button>
                                              )}
                                            </div>
                                            <div className="flex items-center">
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const value =
                                                    localValues[credKey] ?? "";
                                                  handleUpdateCredential(
                                                    cred._id,
                                                    {
                                                      label: cred.label,
                                                      value,
                                                    }
                                                  );
                                                }}
                                                disabled={isSaving}
                                                className="h-8 px-3 text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-blue-600 dark:hover:bg-blue-500 hover:text-white rounded-lg transition-all duration-200 disabled:opacity-50 whitespace-nowrap"
                                              >
                                                {isSaving ? "..." : "Save"}
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  handleDeleteCredential(
                                                    cred._id
                                                  )
                                                }
                                                disabled={credentialLoading}
                                                className="h-8 flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200 w-0 opacity-0 group-hover:w-8 group-hover:opacity-100 group-hover:ml-2 overflow-hidden"
                                                title="Delete"
                                              >
                                                <Trash2 className="w-4 h-4 flex-shrink-0" />
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    });
                                  })()}
                              </div>

                              {/* CMS Only: Add Custom Field */}
                              {catKey === "cms" && (
                                <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
                                  {!showCmsCustomField ? (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setShowCmsCustomField(true)
                                      }
                                      className="h-8 px-3 text-xs font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors flex items-center gap-1.5"
                                    >
                                      <Plus className="w-3.5 h-3.5" />
                                      Add Custom CMS Field
                                    </button>
                                  ) : (
                                    <div className="space-y-3 bg-white dark:bg-slate-900 p-3 rounded-lg border border-purple-100 dark:border-purple-900/30">
                                      <div className="grid grid-cols-[160px_1fr] gap-3 items-center">
                                        <input
                                          type="text"
                                          value={newCmsField.label}
                                          onChange={(e) =>
                                            setNewCmsField((prev) => ({
                                              ...prev,
                                              label: e.target.value,
                                            }))
                                          }
                                          placeholder="Field Label (e.g. CMS API Key)"
                                          className="h-8 px-3 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-black dark:text-white rounded-lg focus:outline-none"
                                        />
                                        <div className="flex gap-2">
                                          <input
                                            type="text"
                                            value={newCmsField.value}
                                            onChange={(e) =>
                                              setNewCmsField((prev) => ({
                                                ...prev,
                                                value: e.target.value,
                                              }))
                                            }
                                            placeholder="Field Value"
                                            className="flex-1 h-8 px-3 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-black dark:text-white rounded-lg focus:outline-none"
                                          />
                                          <button
                                            type="button"
                                            onClick={() => {
                                              if (newCmsField.label.trim()) {
                                                handleAddCredential(newCmsField);
                                                setNewCmsField({
                                                  label: "",
                                                  value: "",
                                                });
                                                setShowCmsCustomField(false);
                                              }
                                            }}
                                            className="h-8 px-3 text-xs font-medium bg-purple-600 text-white hover:bg-purple-700 rounded-lg transition-colors flex items-center justify-center"
                                          >
                                            Add
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setShowCmsCustomField(false);
                                              setNewCmsField({
                                                label: "",
                                                value: "",
                                              });
                                            }}
                                            className="h-8 px-3 text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors flex items-center justify-center"
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}

                      {/* Other Credentials Category (for labels that don't match any template) */}
                      {(() => {
                        const categorized = categorizeCredentials();
                        if (categorized.other.length === 0) return null;

                        const colors = getColorClasses("gray");
                        const IconComponent = Key;

                        return (
                          <div className="bg-gray-50 dark:bg-slate-700/30 rounded-lg p-4 border border-gray-200 dark:border-slate-700">
                            <div className="flex items-center gap-2 mb-4">
                              <div
                                className={`w-8 h-8 ${colors.icon} rounded-lg flex items-center justify-center`}
                              >
                                <IconComponent className="w-4 h-4 text-white" />
                              </div>
                              <h4 className="text-base font-semibold text-slate-800 dark:text-white">
                                Other Project Info
                              </h4>
                            </div>

                            <div className="space-y-3">
                              {categorized.other.map((cred) => {
                                const credKey = cred.label.toLowerCase();
                                const isSaving =
                                  savingCredentialKey === credKey;
                                return (
                                  <div
                                    key={cred._id}
                                    className="grid grid-cols-[160px_1fr] gap-3 items-center group"
                                  >
                                    <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                                      {cred.label}
                                    </label>
                                    <div className="flex items-center gap-2">
                                      <div className="relative flex-1">
                                        <input
                                          type={cred.isRevealed || !cred.isEncrypted ? "text" : "password"}
                                          value={localValues[credKey] ?? ""}
                                          onChange={(e) =>
                                            setLocalValues((prev) => ({
                                              ...prev,
                                              [credKey]: e.target.value,
                                            }))
                                          }
                                          placeholder="Enter value..."
                                          className={`w-full h-8 px-3 ${cred.isEncrypted ? "pr-10" : ""} text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent placeholder:text-slate-400 dark:placeholder:text-slate-600 transition-colors`}
                                          disabled={isSaving}
                                        />
                                        {cred.isEncrypted && (
                                          <button
                                            type="button"
                                            onClick={() => handleRevealCredential(cred._id)}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                                            title={cred.isRevealed ? "Hide encrypted data" : "Reveal encrypted data"}
                                          >
                                            {cred.isRevealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                          </button>
                                        )}
                                      </div>
                                      <div className="flex items-center">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const value =
                                              localValues[credKey] ?? "";
                                            handleUpdateCredential(cred._id, {
                                              label: cred.label,
                                              value,
                                            });
                                          }}
                                          disabled={isSaving}
                                          className="h-8 px-3 text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-blue-600 dark:hover:bg-blue-500 hover:text-white rounded-lg transition-all duration-200 disabled:opacity-50 whitespace-nowrap"
                                        >
                                          {isSaving ? "..." : "Save"}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleDeleteCredential(cred._id)
                                          }
                                          disabled={credentialLoading}
                                          className="h-8 flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200 w-0 opacity-0 group-hover:w-8 group-hover:opacity-100 group-hover:ml-2 overflow-hidden"
                                          title="Delete"
                                        >
                                          <Trash2 className="w-4 h-4 flex-shrink-0" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Add Custom Label Button */}
                      <div className="bg-white dark:bg-slate-800 rounded-lg p-6 border border-dashed border-slate-300 dark:border-slate-600 text-center">
                        <h4 className="text-sm font-semibold text-slate-800 dark:text-white mb-2">
                          Add Custom Credential
                        </h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                          Need to store something else? Create a custom label.
                        </p>
                        <div className="flex gap-2 max-w-xl mx-auto">
                          <input
                            type="text"
                            value={newCredential.label}
                            onChange={(e) =>
                              setNewCredential((prev) => ({
                                ...prev,
                                label: e.target.value,
                              }))
                            }
                            placeholder="Label (e.g. Hosting Key)"
                            className="flex-1 h-8 px-3 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <input
                            type="text"
                            value={newCredential.value}
                            onChange={(e) =>
                              setNewCredential((prev) => ({
                                ...prev,
                                value: e.target.value,
                              }))
                            }
                            placeholder="Value"
                            className="flex-1 h-8 px-3 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <button
                            type="button"
                            onClick={() => handleAddCredential()}
                            disabled={credentialLoading}
                            className="h-8 px-4 text-xs font-medium bg-slate-800 dark:bg-slate-700 text-white hover:bg-slate-900 dark:hover:bg-slate-600 rounded-lg transition-colors flex items-center gap-1.5"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            Add
                          </button>
                        </div>
                      </div>

                      {/* Credential Access Management */}
                      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden transition-colors">
                        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            <h4 className="text-base font-semibold text-slate-800 dark:text-white">
                              Access Management
                            </h4>
                          </div>
                          <span className="text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">
                            {credentialAccess.length} Active Users
                          </span>
                        </div>

                        <div className="p-6">
                          <div className="space-y-6">
                            {/* Currently with Access */}
                            <div>
                              <h5 className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-3">
                                Members with Access
                              </h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {credentialAccess.map((access) => {
                                  const userData =
                                    getCredentialAccessUserDetails(access);
                                  const isUserAdmin =
                                    userData.role === "admin";

                                  return (
                                    <div
                                      key={userData._id || userData.id}
                                      className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/30 rounded-lg border border-slate-100 dark:border-slate-700 group transition-all"
                                    >
                                      <div className="flex items-center gap-3">
                                        <Avatar user={userData} size="sm" />
                                        <div>
                                          <p className="text-sm font-semibold text-slate-800 dark:text-white">
                                            {userData.name}
                                          </p>
                                          <p className="text-xs text-slate-500 dark:text-slate-400">
                                            {userData.email}
                                          </p>
                                        </div>
                                      </div>
                                      {!isUserAdmin ? (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleRevokeAccess(
                                              userData._id || userData.id
                                            )
                                          }
                                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                                          title="Revoke Access"
                                        >
                                          <UserMinus className="w-4 h-4" />
                                        </button>
                                      ) : (
                                        <div
                                          className="p-2 text-blue-600 dark:text-blue-400"
                                          title="Admins have permanent access"
                                        >
                                          <Shield className="w-4 h-4" />
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Grant New Access */}
                            {membersWithoutAccess.length > 0 && (
                              <div className="pt-6 border-t border-slate-100 dark:border-slate-700">
                                <h5 className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-3">
                                  Grant New Access
                                </h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {membersWithoutAccess.map((member) => (
                                    <button
                                      key={member.user._id || member.user.id}
                                      type="button"
                                      onClick={() =>
                                        handleGrantAccess(
                                          member.user._id || member.user.id
                                        )
                                      }
                                      className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg border border-slate-200 dark:border-slate-700 group transition-all text-left"
                                    >
                                      <div className="flex items-center gap-3">
                                        <Avatar user={member.user} size="sm" />
                                        <div>
                                          <p className="text-sm font-semibold text-slate-800 dark:text-white">
                                            {member.user.name}
                                          </p>
                                          <p className="text-xs text-slate-500 dark:text-slate-400">
                                            {member.user.email}
                                          </p>
                                        </div>
                                      </div>
                                      <UserPlus className="w-4 h-4 text-slate-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" />
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Member: View-only Credentials List */}
                  {!isAdmin && (
                    <div className="grid grid-cols-1 gap-6">
                      {Object.entries(categorizeCredentials()).map(
                        ([catKey, creds]) => {
                          if (creds.length === 0) return null;
                          const category = credentialCategories[catKey];
                          const IconComponent = category.icon;
                          const colors = getColorClasses(category.color);

                          return (
                            <div
                              key={catKey}
                              className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden"
                            >
                              <div
                                className={`px-6 py-4 ${colors.bg} border-b ${colors.border} flex items-center gap-3`}
                              >
                                <div
                                  className={`w-10 h-10 ${colors.icon} rounded-lg flex items-center justify-center shadow-lg`}
                                >
                                  <IconComponent className="w-5 h-5 text-white" />
                                </div>
                                <h4
                                  className={`text-lg font-bold ${colors.text}`}
                                >
                                  {category.name}
                                </h4>
                              </div>

                              <div className="divide-y divide-slate-100 dark:divide-slate-700">
                                {creds.map((cred) => {
                                  const isPassword = cred.label
                                    .toLowerCase()
                                    .includes("password");
                                  const isVisible =
                                    visiblePasswords[cred._id] || !isPassword;
                                  const hasValue = cred.value?.trim();

                                  return (
                                    <div
                                      key={cred._id}
                                      className="p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors group"
                                    >
                                      <div className="flex items-center justify-between gap-4">
                                        <div className="flex-1 min-w-0">
                                          <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
                                            {cred.label}
                                          </p>
                                          <div className="flex items-center gap-2">
                                            <p
                                              className={`text-sm font-semibold truncate ${
                                                hasValue
                                                  ? "text-slate-800 dark:text-slate-200"
                                                  : "text-slate-300 dark:text-slate-600 italic font-normal"
                                              }`}
                                            >
                                              {hasValue
                                                ? isVisible || cred.isRevealed
                                                  ? cred.value
                                                  : "••••••••••••"
                                                : "Not set"}
                                            </p>
                                            {(isPassword || (cred.isEncrypted)) && hasValue && (
                                              <button
                                                onClick={() => {
                                                  if (cred.isEncrypted) {
                                                    handleRevealCredential(cred._id);
                                                  } else {
                                                    togglePasswordVisibility(cred._id);
                                                  }
                                                }}
                                                className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                                                title={cred.isEncrypted ? (cred.isRevealed ? "Hide encrypted data" : "Reveal encrypted data") : (isVisible ? "Hide password" : "Show password")}
                                              >
                                                {cred.isRevealed || (isVisible && !cred.isEncrypted) ? (
                                                  <EyeOff className="w-3.5 h-3.5" />
                                                ) : (
                                                  <Eye className="w-3.5 h-3.5" />
                                                )}
                                              </button>
                                            )}
                                          </div>
                                        </div>

                                        {hasValue && (
                                          <div className="flex items-center gap-1">
                                            {cred.value?.startsWith("http") && (
                                              <a
                                                href={cred.value}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="p-2 text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all"
                                                title="Open URL"
                                              >
                                                <ExternalLink className="w-4 h-4" />
                                              </a>
                                            )}
                                            <button
                                              onClick={() =>
                                                handleCopy(cred.value, cred._id)
                                              }
                                              className="p-2 text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all"
                                              title="Copy to clipboard"
                                            >
                                              {copiedField === cred._id ? (
                                                <Check className="w-4 h-4 text-green-500" />
                                              ) : (
                                                <Copy className="w-4 h-4" />
                                              )}
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  )}
                </div>
              )}

            {/* DESCRIPTIONS TAB */}
            {activeTab === "descriptions" && (
              <div className="space-y-6">
                {/* Add Description Note */}
                <div className="bg-gray-50 dark:bg-slate-700/30 rounded-lg p-4 border border-gray-200 dark:border-slate-700 transition-colors">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                      <Plus className="w-4 h-4 text-white" />
                    </div>
                    <h4 className="text-base font-semibold dark:text-white">Add Project Note</h4>
                  </div>

                  <div className="space-y-4">
                    <div className="border border-gray-300 dark:border-slate-600 rounded-lg overflow-hidden bg-white dark:bg-slate-900 transition-colors">
                      <SimpleQuillEditor
                        value={newDescription}
                        onChange={setNewDescription}
                        placeholder="Type your project note here..."
                        height="150px"
                      />
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={handleAddDescription}
                        disabled={descriptionLoading || !newDescription.trim()}
                        className="h-8 px-4 bg-purple-600 text-white font-medium text-xs rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 shadow flex items-center justify-center"
                      >
                        {descriptionLoading ? "Saving..." : "Add Note"}
                      </button>
                    </div>
                  </div>
                </div>

                {/* List of Descriptions */}
                <div className="space-y-4">
                  <h4 className="text-sm font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Project Notes ({descriptions.length})
                  </h4>

                  {descriptions.length === 0 ? (
                    <div className="text-center py-12 bg-slate-50 dark:bg-slate-700/30 rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-700">
                      <FileText className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                      <p className="text-slate-500 dark:text-slate-400 font-medium">
                        No project notes yet
                      </p>
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                        Use notes to keep track of project-specific information
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {descriptions
                        .sort(
                          (a, b) =>
                            new Date(b.createdAt) - new Date(a.createdAt)
                        )
                        .map((desc) => (
                          <div
                            key={desc._id}
                            className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden group transition-colors"
                          >
                            {editingDescriptionId === desc._id ? (
                              /* Edit Mode */
                              <div className="p-4 space-y-4">
                                <div className="border border-blue-200 dark:border-blue-800 rounded-lg overflow-hidden bg-white dark:bg-slate-900 transition-colors">
                                  <SimpleQuillEditor
                                    value={editingDescriptionContent}
                                    onChange={setEditingDescriptionContent}
                                    placeholder="Edit description..."
                                    height="200px"
                                    autoFocus
                                  />
                                </div>
                                <div className="flex justify-end gap-2">
                                  <button
                                    type="button"
                                    onClick={cancelEditingDescription}
                                    className="h-8 px-3 text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center justify-center"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleUpdateDescription(desc._id)
                                    }
                                    disabled={descriptionLoading}
                                    className="h-8 px-3 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm flex items-center justify-center"
                                  >
                                    {descriptionLoading
                                      ? "Saving..."
                                      : "Save Changes"}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              /* View Mode */
                              <div className="p-5">
                                <div className="flex items-center justify-between mb-4">
                                  <div className="flex items-center gap-3">
                                    <Avatar user={getNoteAuthor(desc)} size="sm" />
                                    <div>
                                      <p className="text-sm font-bold text-slate-800 dark:text-white">
                                        {getNoteAuthor(desc)?.name || "Unknown"}
                                      </p>
                                      <p className="text-[10px] text-slate-400 dark:text-slate-500 font-medium uppercase tracking-tight">
                                        {new Date(
                                          desc.createdAt
                                        ).toLocaleString("en-US", {
                                          month: "short",
                                          day: "2-digit",
                                          year: "numeric",
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                      type="button"
                                      onClick={() => startEditingDescription(desc)}
                                      className="p-2 text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all"
                                      title="Edit Note"
                                    >
                                      <Edit className="w-4 h-4" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleDeleteDescription(desc._id)
                                      }
                                      className="p-2 text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
                                      title="Delete Note"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                                <div
                                  className="prose prose-sm dark:prose-invert max-w-none text-slate-700 dark:text-slate-300"
                                  style={{
                                    lineHeight: "1.6",
                                    fontSize: "15px",
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: desc.content,
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sticky Footer */}
        {activeTab === "info" && (
          <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 px-6 py-3 transition-colors duration-200">
            <div className="flex justify-end items-center space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="h-8 px-4 text-gray-600 dark:text-slate-300 hover:text-gray-800 dark:hover:text-white font-medium text-xs transition-all duration-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 border border-gray-300 dark:border-slate-600 flex items-center justify-center"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  await handleSubmit();
                }}
                className="h-8 bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 font-medium text-xs px-4 rounded-lg transition-all duration-200 flex items-center space-x-1.5 disabled:opacity-50 disabled:cursor-not-allowed shadow hover:shadow-md"
                disabled={loading || editMode}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Save Project Details</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        type={confirmModal.type}
        isLoading={credentialLoading || descriptionLoading}
      />
    </div>
  );
};

export default EditProjectModal;
